import { useCallback, useEffect, useRef, useState } from 'react';
import { GenericAbortSignal } from 'axios';
import { DEFAULT_CATALOG_PAGE } from 'core/constants/catalogue.constants';
import { FIRST_LOAD_CATALOG_LIST_SKELETON } from 'core/constants/skeletons.constants';
import { ICatalogSortOption } from 'core/model/interfaces/catalogues.interface';
import { IPaginatedProducts } from 'core/model/interfaces/product.interface';
import { catalogsService } from 'core/services/catalogs/catalogs.service';
import { checkoutCustomerSelector } from 'core/store/checkout/selectors';
import { useAppSelector } from 'core/store/store.utils';

interface IFetchProductsParams {
  page: number;
  restart?: boolean;
  customerId: number;
  signal?: GenericAbortSignal;
}

interface IUsePaginatedProductsParams {
  orderBy?: ICatalogSortOption;
  navigationSubItemId?: number;
  query?: string;
  hasPromotion?: boolean;
}

const initialPaginatedProducts: IPaginatedProducts = {
  pagination: {
    currentPage: 1,
    totalItems: 0,
    totalPages: 0,
    itemsPerPage: FIRST_LOAD_CATALOG_LIST_SKELETON,
  },
  products: [],
};

export const usePaginatedProducts = ({
  navigationSubItemId,
  orderBy,
  query,
  hasPromotion,
}: IUsePaginatedProductsParams) => {
  const customerId = useAppSelector(checkoutCustomerSelector);
  const [paginatedProducts, setPaginatedProducts] = useState<IPaginatedProducts>(initialPaginatedProducts);
  const [loading, setLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setPaginatedProducts(prev => ({ ...prev, products: [] }));
  }, [orderBy]);

  useEffect(() => {
    setPaginatedProducts(initialPaginatedProducts);
  }, [navigationSubItemId, query]);

  const fetchProducts = useCallback(
    async ({ page, restart = false, customerId, signal }: IFetchProductsParams) => {
      if (!navigationSubItemId && !query && !hasPromotion) return;
      if (customerId) {
        try {
          setLoading(true);
          const { pagination, products } = await catalogsService.getListProducts(
            {
              navigationSubItemId: Number.isNaN(navigationSubItemId) ? undefined : navigationSubItemId,
              itemsPerPage: FIRST_LOAD_CATALOG_LIST_SKELETON,
              page,
              sortBy: orderBy?.value,
              q: query,
              hasPromotion,
            },
            customerId,
            signal,
          );

          if (!isMounted.current) return;

          pagination.currentPage = page;
          setPaginatedProducts(oldProducts => ({
            pagination,
            products: restart ? products : [...oldProducts.products, ...products],
          }));
          setLoading(false);
        } catch (error) {
          if (!isMounted.current) return error;

          setLoading(false);
        }
      }
    },
    [hasPromotion, navigationSubItemId, orderBy?.value, query],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    if (customerId) fetchProducts({ restart: true, page: DEFAULT_CATALOG_PAGE, customerId, signal });
    return () => {
      abortController.abort();
    };
  }, [fetchProducts, customerId]);

  const lastBookElementRef = useCallback(
    (node: Element | null) => {
      if (observer.current) observer.current.disconnect();
      const { pagination, products } = paginatedProducts;
      observer.current = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          products.length < pagination.totalItems &&
          products.length > 0 &&
          !loading &&
          pagination.currentPage < pagination.totalPages &&
          customerId
        )
          fetchProducts({ page: pagination.currentPage + 1, customerId });
      });
      if (node) observer.current.observe(node);
    },
    [paginatedProducts, loading, fetchProducts, customerId],
  );

  return { paginatedProducts, loading, lastBookElementRef };
};
